import { useState } from "react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Target, Calendar, BookOpen, CheckCircle, ListTodo, Brain, 
  Heart, Sparkles, FolderOpen, Music, Users, Trophy, 
  Bell, Clock, BarChart3, Lightbulb, PenTool, Kanban,
  Clipboard, CalendarClock, Bot, LineChart, Mic, Zap,
  ShieldCheck, Lock, Contact, Activity, Gauge, Utensils,
  Smartphone
} from "lucide-react";
import type { ResellerConfig } from "@shared/schema";
import LoginDialog from "@/components/login-dialog";

const features = [
  {
    icon: Target,
    title: "Goal Management",
    description: "Create, track, and achieve goals with milestones, progress tracking, S.M.A.R.T. qualification, and time cost analysis.",
    color: "text-blue-600"
  },
  {
    icon: ListTodo,
    title: "Task Management",
    description: "Comprehensive task system with sub-tasks, drag-and-drop reordering, filtering, grouping, and Top Three daily priorities.",
    color: "text-green-600"
  },
  {
    icon: Kanban,
    title: "Kanban Board",
    description: "Visual task management with custom color-coded columns, drag-and-drop cards, and seamless view toggling.",
    color: "text-purple-600"
  },
  {
    icon: CheckCircle,
    title: "Habit Tracking",
    description: "Build lasting habits with customizable targets, streak visualization, multiple view modes, and goal linking.",
    color: "text-emerald-600"
  },
  {
    icon: Calendar,
    title: "Calendar Integration",
    description: "Two-way Google Calendar sync, iCal feed support, recurring events, and PDF export with multiple views.",
    color: "text-indigo-600"
  },
  {
    icon: BookOpen,
    title: "Smart Journaling",
    description: "Rich text editor with AI insights, task extraction, writing help, version history, templates, and stylus drawing.",
    color: "text-amber-600"
  },
  {
    icon: PenTool,
    title: "Notepad",
    description: "Enterprise-level notes with rich text, collapsible sections, tag management, bidirectional linking, and sharing.",
    color: "text-rose-600"
  },
  {
    icon: Clipboard,
    title: "Checklists",
    description: "Comprehensive checklists with templates, progress tracking, drag-and-drop reordering, and active/completed tabs.",
    color: "text-cyan-600"
  },
  {
    icon: CalendarClock,
    title: "Planner",
    description: "Time-horizon planning organizing items from Today through Future with category filtering and drag-and-drop.",
    color: "text-teal-600"
  },
  {
    icon: Heart,
    title: "Gratitude Journal",
    description: "Daily gratitude entries with auto-save, drag-and-drop reordering, and reflection tracking.",
    color: "text-pink-600"
  },
  {
    icon: Music,
    title: "Meditation & Media",
    description: "Create playlists, custom meditations with text-to-speech, YouTube support, and session time tracking.",
    color: "text-violet-600"
  },
  {
    icon: FolderOpen,
    title: "Media Library",
    description: "Upload and manage files with metadata, goal connections, and Google Drive integration.",
    color: "text-orange-600"
  },
  {
    icon: Bot,
    title: "Zeus AI Assistant",
    description: "Conversational AI that creates goals, habits, tasks, journal entries, and notes through guided dialogue — one clear question at a time.",
    color: "text-sky-600"
  },
  {
    icon: Mic,
    title: "Zeus Voice Chat",
    description: "Talk to Zeus hands-free with natural back-and-forth conversation, premium studio-quality AI voices, adjustable speaking speed, and instant interruption.",
    color: "text-orange-600"
  },
  {
    icon: Zap,
    title: "AI Automation Rules",
    description: "Set up agentic rules Zeus runs for you — scheduled summaries, automated follow-ups, and delegated agent tasks.",
    color: "text-yellow-500"
  },
  {
    icon: ShieldCheck,
    title: "AI Guardrails",
    description: "Configurable behavioral guardrails and reporting keep your AI assistant working exactly within the boundaries you set.",
    color: "text-lime-600"
  },
  {
    icon: Lock,
    title: "Secure PII Vault",
    description: "Password-protected vault for sensitive records with automatic masking so private data never reaches AI services.",
    color: "text-red-600"
  },
  {
    icon: Contact,
    title: "Contacts",
    description: "Full contact management with categories, document attachments, and VCF/CSV import and export.",
    color: "text-cyan-500"
  },
  {
    icon: Activity,
    title: "Health Metrics",
    description: "Log weight, sleep, and other health metrics with trend analysis to keep wellness goals on track.",
    color: "text-green-500"
  },
  {
    icon: Gauge,
    title: "Custom Trackers",
    description: "Track any number that matters to you with TrackerX — custom measured data points linked to your goals.",
    color: "text-purple-600"
  },
  {
    icon: Utensils,
    title: "Meal & Workout Plans",
    description: "Specialized planners for nutrition and fitness so health goals come with a concrete daily plan.",
    color: "text-rose-500"
  },
  {
    icon: Smartphone,
    title: "Mobile App",
    description: "Take GoalVia everywhere with the companion mobile app featuring push notifications and on-the-go access.",
    color: "text-slate-500"
  },
  {
    icon: Lightbulb,
    title: "Goal Simulation",
    description: "Analyze proposed goals before committing to understand true cost in time, money, and trade-offs.",
    color: "text-yellow-600"
  },
  {
    icon: Users,
    title: "Community & Friends",
    description: "Connect with others, share goals and notes, 1-on-1 messaging, and group chat rooms.",
    color: "text-blue-500"
  },
  {
    icon: Clock,
    title: "Virtual Assistants",
    description: "Invite assistants, share only the individual records they need, and use time tracking and reports.",
    color: "text-slate-600"
  },
  {
    icon: Trophy,
    title: "Achievements",
    description: "Comprehensive achievement tracking with category-based organization, tiered badges, and progress visualization.",
    color: "text-amber-500"
  },
  {
    icon: BarChart3,
    title: "Analytics Dashboard",
    description: "Customizable dashboard with drag-and-drop widgets, productivity insights, and progress visualization.",
    color: "text-emerald-500"
  },
  {
    icon: LineChart,
    title: "Journal Analytics",
    description: "Track writing patterns, mood trends, and productivity insights from your journal entries.",
    color: "text-indigo-500"
  },
  {
    icon: Bell,
    title: "Smart Notifications",
    description: "Configurable email, SMS, and in-app notifications with event-based reminders and daily summaries.",
    color: "text-red-500"
  },
  {
    icon: Sparkles,
    title: "Guided Tours",
    description: "Interactive tutorials with spotlight effects, embedded videos, and persistent progress tracking.",
    color: "text-fuchsia-600"
  },
  {
    icon: Brain,
    title: "AI-Powered Insights",
    description: "Smart recommendations, automated task extraction, and intelligent analysis throughout the app.",
    color: "text-purple-500"
  }
];

export default function Landing() {
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);

  const { data: config } = useQuery<ResellerConfig>({
    queryKey: ["/api/reseller/config"],
    retry: false,
  });

  const companyName = config?.companyName || "GoalVia";
  const heroTitle = config?.heroTitle || "Transform Your Goals Into Achievements";
  const heroSubtitle = config?.heroSubtitle || "The comprehensive productivity platform that combines goal tracking, habit formation, journaling, task management, and AI-powered insights to help you achieve more.";
  const primaryColor = config?.primaryColor || "#3b82f6";
  const showPricing = config?.showPricing ?? true;
  const customPricingMessage = config?.customPricingMessage;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <header className="border-b bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-2">
              {config?.companyLogo ? (
                <img 
                  src={config.companyLogo} 
                  alt={companyName} 
                  className="h-8 w-auto"
                  data-testid="img-company-logo"
                />
              ) : (
                <span className="text-3xl font-bold leading-none">
                  <span className="text-foreground">Goal</span><span className="text-orange-500">Via</span>
                </span>
              )}
              {config?.companyLogo && <span className="text-2xl font-bold" data-testid="text-company-name">{companyName}</span>}
            </div>
            <div className="flex gap-3">
              <Button 
                onClick={() => setLoginDialogOpen(true)} 
                style={{ backgroundColor: primaryColor }}
                data-testid="button-login"
              >
                Log In
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6" data-testid="text-hero-title">
            {heroTitle.split(' ').map((word, index) => {
              const words = heroTitle.split(' ');
              const isLastWord = index === words.length - 1;
              const isSecondToLast = index === words.length - 2 && words.length > 1;
              
              if (isLastWord || (isSecondToLast && words[words.length - 1].length < 4)) {
                return (
                  <span key={index} style={{ color: primaryColor }}>
                    {word}{index < words.length - 1 ? ' ' : ''}
                  </span>
                );
              }
              return word + (index < words.length - 1 ? ' ' : '');
            })}
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto" data-testid="text-hero-subtitle">
            {heroSubtitle}
          </p>
          {customPricingMessage && (
            <p className="text-lg font-medium mb-6 max-w-2xl mx-auto" style={{ color: primaryColor }} data-testid="text-custom-pricing">
              {customPricingMessage}
            </p>
          )}
          <div className="flex gap-4 justify-center flex-wrap">
            <Link href="/signup">
              <Button 
                size="lg" 
                style={{ backgroundColor: primaryColor }}
                data-testid="button-start-free"
              >
                Get Started Free
              </Button>
            </Link>
            {showPricing && (
              <Button 
                variant="outline" 
                size="lg" 
                onClick={() => window.location.href = "/pricing"}
                style={{ borderColor: primaryColor, color: primaryColor }}
                data-testid="button-view-pricing"
              >
                View Pricing
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Motivational Quote Section */}
      <section className="py-12 bg-gradient-to-r from-blue-600 to-indigo-700 dark:from-blue-800 dark:to-indigo-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-center text-white leading-tight">
            Actions - Not Hope - Will Get You To Your Goals
          </h2>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Everything You Need to Succeed
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              A complete suite of productivity tools designed to help you set goals, build habits, stay organized, and achieve more every day.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow duration-200 border-0 shadow-sm bg-white dark:bg-gray-800">
                <CardHeader className="pb-3">
                  <feature.icon className={`h-8 w-8 mb-3 ${feature.color}`} />
                  <CardTitle className="text-lg">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-sm leading-relaxed">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
              Ready to Achieve Your Goals?
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
              Join now and start your journey toward becoming more productive, organized, and successful.
            </p>
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md mx-auto">
              <div className="space-y-4 mb-6">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">Free to get started</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">No credit card required</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">All core features included</span>
                </div>
              </div>
              <Link href="/signup" className="block w-full">
                <Button 
                  className="w-full" 
                  size="lg" 
                  style={{ backgroundColor: primaryColor }}
                  data-testid="button-start-trial"
                >
                  Get Started Now
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              {config?.companyLogo ? (
                <img 
                  src={config.companyLogo} 
                  alt={companyName} 
                  className="h-6 w-auto"
                  data-testid="img-footer-logo"
                />
              ) : (
                <span className="text-2xl font-bold leading-none">
                  <span className="text-foreground">Goal</span><span className="text-orange-500">Via</span>
                </span>
              )}
              {config?.companyLogo && <span className="text-xl font-bold" data-testid="text-footer-company">{companyName}</span>}
            </div>
            <p className="text-gray-400 text-sm">
              &copy; {new Date().getFullYear()} {companyName}. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      <LoginDialog open={loginDialogOpen} onOpenChange={setLoginDialogOpen} />
    </div>
  );
}
